import { Box, Button, Paper, Stack, Typography } from '@mui/material';

import type { BillingCatalogDto, CompanyBillingSummaryDto } from '@shared/services/apiService';

import { formatBillingMoney, formatCreditUnits } from '../model/formatBillingUnits';

export const BillingPlanOffers = ({
  summary,
  catalog,
  busy,
  onSubscribe,
  onUpgrade,
  onDowngrade,
}: {
  summary: CompanyBillingSummaryDto;
  catalog: BillingCatalogDto;
  busy: boolean;
  onSubscribe: (planId: string) => void;
  onUpgrade: (planId: string) => void;
  onDowngrade: (planId: string, planName: string) => void;
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', md: 'repeat(3,minmax(0,1fr))' },
      gap: 2,
    }}>
    {catalog.plans.map((plan) => (
      <Paper
        key={plan.id}
        variant="outlined"
        sx={{ p: 3, borderRadius: 3, display: 'flex', flexDirection: 'column' }}>
        <Stack spacing={2}>
          <Typography variant="h5" fontWeight={600}>
            {plan.key}
          </Typography>
          <Typography variant="h4" fontWeight={600}>
            {formatBillingMoney(plan.amountMinor, plan.currency, plan.currencyScale)} / month
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Monthly recurring amount. Review taxes and the final amount before payment.
          </Typography>
          <Typography>
            {formatCreditUnits(plan.allowanceUnits, plan.creditScale)} credits per period
          </Typography>
          <Typography color="text.secondary">{plan.concurrentLimit} simultaneous calls</Typography>
          {summary.status === 'paid' && plan.id === summary.plan?.id ? (
            <Typography color="primary">Current plan</Typography>
          ) : summary.canManage && catalog.canCheckout ? (
            <Button variant="contained" disabled={busy} onClick={() => onSubscribe(plan.id)}>
              Subscribe
            </Button>
          ) : summary.canManage &&
            catalog.canChangePlan &&
            summary.plan &&
            BigInt(plan.allowanceUnits) > BigInt(summary.plan.allowanceUnits) ? (
            <Button variant="outlined" disabled={busy} onClick={() => onUpgrade(plan.id)}>
              Review upgrade
            </Button>
          ) : summary.canManage &&
            catalog.canChangePlan &&
            summary.plan &&
            plan.creditScale === summary.plan.creditScale &&
            BigInt(plan.allowanceUnits) < BigInt(summary.plan.allowanceUnits) ? (
            <Button
              variant="outlined"
              disabled={busy}
              onClick={() => onDowngrade(plan.id, plan.key)}>
              Schedule lower plan
            </Button>
          ) : null}
        </Stack>
      </Paper>
    ))}
  </Box>
);
